import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import _ from 'lodash';
import { CountdownTimer, Svg, ParsedMs } from '@tradingblock/components';
import {
  MarketCloseTimeSelector,
  MarketOpenTimeSelector,
  isMarketOpenSelector,
  useStateSelector,
} from '../../../data/global/dataSelectors';
import { DisplayTimeTilClose, isToday } from '../../../utilities/date';
import { useDispatch } from 'react-redux';
import { AccountActions } from '../../../data/global/actions/AccountActions';
import { MarketDataEntitlementTypeId } from '@tradingblock/types';
import { InfoDataActions } from '../../../data/global/actions/InfoActions';
import { AccountApiFactory } from '@tradingblock/api';
import Config from '../../../config';
import { DataActions } from '../../../data/global/actions';

dayjs.extend(relativeTime);

export const DashboardMarketClosedCountdown: FC<{}> = ({}) => {
  const dispatch = useDispatch();
  const latestReceivedDate = useStateSelector(s => s.quotes.quoteMetadata.latestReceivedTime);
  const marketDataEntitlementTypeId = useStateSelector(
    s =>
      s.account.profile.current &&
      s.account.profile.current.entitlement &&
      s.account.profile.current.entitlement.marketDataEntitlementTypeId
  );
  const isExpired = useStateSelector(s => s.auth.isExpired);

  const origin = useStateSelector(s => s.auth.origin);
  const siteConfigId = origin === 'mb' ? 4 : 2;
  const marketOpen = useStateSelector(s => isMarketOpenSelector(s));
  const marketOpenTime = useStateSelector(s => MarketOpenTimeSelector(s));
  const marketCloseTime = useStateSelector(s => MarketCloseTimeSelector(s));
  const accountId = useStateSelector(state => state.account.accountId);
  const token = useStateSelector(state => state.auth.apiToken);
  const selectedSubAccountId = useStateSelector(state => state.account.subAccountId);
  const apiRootUrl = Config.tradingApi;

  const accountApi = useMemo(() => {
    if (accountId && token && !selectedSubAccountId) {
      return AccountApiFactory(apiRootUrl, token, accountId);
    }
    if (accountId && apiRootUrl && token && selectedSubAccountId) {
      return AccountApiFactory(apiRootUrl, token, accountId, selectedSubAccountId);
    }
  }, [accountId, token, selectedSubAccountId]);

  const countdownTitle = useCallback(
    (parsed: ParsedMs) => {
      const marketClosed =
        parsed.hours <= 0 && parsed.minutes <= 0
          ? 'NYSE Market closed'
          : `NYSE Market closes in ${DisplayTimeTilClose(parsed)}`;

      const marketOpens = `NYSE Market opens in ${DisplayTimeTilClose(parsed)}`;
      let quoteDataAsOf = 'Data refresh time not available';
      if (!_.isNil(latestReceivedDate)) {
        const now = dayjs().valueOf();
        if (now <= latestReceivedDate) {
          quoteDataAsOf = 'Data as of a few seconds ago';
        } else {
          quoteDataAsOf = `Data as of ${dayjs(latestReceivedDate).fromNow()}`;
        }
      }
      if (marketOpen) {
        return `${marketClosed}. ${quoteDataAsOf}.`;
      } else {
        return `${marketOpens}. ${quoteDataAsOf}.`;
      }
    },
    [latestReceivedDate, marketOpen]
  );

  useEffect(() => {
    dispatch(AccountActions.requestAccountEntitlement({}));
    dispatch(InfoDataActions.requestAlertMessages({ siteConfigId }));
  }, []);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined = undefined;
    if (!isExpired) {
      intervalId = setInterval(function() {
        dispatch(InfoDataActions.requestAlertMessages({ siteConfigId }));
      }, 60 * 1000 * 5);
    }

    return () => {
      if (intervalId !== undefined) {
        clearInterval(intervalId);
      }
    };
  }, [isExpired, marketOpen]);

  const isDelayedDataUser = marketDataEntitlementTypeId === MarketDataEntitlementTypeId.DelayedQuotesOnlyUser;

  useEffect(() => {
    const requestPositions = async () => {
      if (accountApi && !isExpired) {
        try {
          const positions = await accountApi.positions();
          dispatch(DataActions.receivePositions(positions.payload));
        } catch (err) {
          console.error('Error fetching positions:', err);
        }
      }
    };

    // Request positions immediately when market state changes
    requestPositions();

    const transitionWindow = 5; // minutes before market open/close
    const now = dayjs();
    let intervalId: NodeJS.Timeout | undefined;
    let timeoutId: NodeJS.Timeout | undefined;

    const startIntervalAtNextMinute = () => {
      const now = new Date();
      const msUntilNextMinute = (60 - now.getSeconds()) * 1000;

      timeoutId = setTimeout(() => {
        requestPositions();
        intervalId = setInterval(requestPositions, 60 * 1000);
      }, msUntilNextMinute);
    };

    // Adjusted market times for delayed users
    const adjustedOpenTime = isDelayedDataUser ? dayjs(marketOpenTime).add(20, 'minute') : dayjs(marketOpenTime);

    const adjustedCloseTime = isDelayedDataUser ? dayjs(marketCloseTime).add(20, 'minute') : dayjs(marketCloseTime);

    if (marketOpen) {
      const minutesToClose = adjustedCloseTime.diff(now, 'minute');

      if (minutesToClose <= transitionWindow) {
        startIntervalAtNextMinute();
      }
    } else {
      const minutesToOpen = adjustedOpenTime.diff(now, 'minute');

      if (minutesToOpen <= transitionWindow) {
        startIntervalAtNextMinute();
      }
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [marketOpen, marketOpenTime, marketCloseTime, isExpired, accountApi, isDelayedDataUser]);

  const isMarketOpenToday = (): boolean => {
    if (!marketOpenTime) return false;
    return isToday(marketOpenTime.toDate());
  };

  return (
    <>
      {marketOpen && (
        <CountdownTimer
          title={countdownTitle}
          format={parsed =>
            parsed.hours > 0 || parsed.days > 0
              ? `${parsed.days > 0 ? parsed.days + ':' : ''}${parsed.hours}${
                  parsed.days > 0 ? '' : `:${parsed.minutes < 10 ? '0' : ''}${parsed.minutes}`
                } ${parsed.days > 0 ? 'DAYS' : 'HRS'}`
              : parsed.minutes <= 0
              ? 'CLOSED'
              : `${parsed.minutes} MINS`
          }
          until={dayjs(marketCloseTime)
            .add(1, 'minutes')
            .toDate()}
          className={'dashboard-nav-tool dashboard-nav-time hide-mobile'}
        >
          {isDelayedDataUser && (
            <span
              style={{
                marginRight: 8,
              }}
            >
              20 MIN DELAYED DATA: QUOTE, BID, ASK, OR LAST PRICE
            </span>
          )}
          <span className="tb-icon">
            <Svg path="icon-clock" />
          </span>
        </CountdownTimer>
      )}
      {!marketOpen && (
        <CountdownTimer
          title={countdownTitle}
          format={parsed =>
            isMarketOpenToday()
              ? parsed.hours > 0
                ? `Market Opens in ${parsed.days > 0 ? parsed.days + ':' : ''}${parsed.hours}${
                    parsed.days > 0 ? '' : `:${parsed.minutes < 10 ? '0' : ''}${parsed.minutes}`
                  } ${parsed.days > 0 ? 'DAYS' : 'HRS'}`
                : parsed.minutes <= 0
                ? 'OPEN'
                : `Market Opens in ${parsed.minutes} MINS`
              : 'Closed'
          }
          until={dayjs(marketOpenTime)
            .add(1, 'minutes')
            .toDate()}
          className={'dashboard-nav-tool dashboard-nav-time hide-mobile'}
        >
          <span className="tb-icon">
            <Svg path="icon-clock" />
          </span>
        </CountdownTimer>
      )}
    </>
  );
};
